import { Bot, Send, FileText, CheckCircle2, Mic } from "lucide-react";
import { useTranslation } from "react-i18next";

export const AIAssistantHeroMockup = () => {
  const { t } = useTranslation();
  return (
  <div className="bg-[#0f172a] rounded-2xl shadow-2xl p-5 w-full max-w-md border border-white/10" dir="ltr">
    {/* Header */}
    <div className="flex items-center gap-3 mb-5">
      <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center">
        <Bot className="w-5 h-5 text-primary" />
      </div>
      <div>
        <p className="text-white font-semibold text-sm">{t('aiMockup.heroTitle')}</p>
        <p className="text-white/40 text-xs">{t('aiMockup.heroSubtitle')}</p>
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-emerald-400 text-xs font-medium">{t('aiMockup.online')}</span>
      </div>
    </div>

    {/* Chat messages */}
    <div className="space-y-3 mb-4">
      <div className="flex justify-end">
        <div className="bg-primary/20 text-primary rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[80%]">
          <p className="text-sm">{t('aiMockup.userQ1')}</p>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="w-6 h-6 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0 mt-1">
          <Bot className="w-3.5 h-3.5 text-primary" />
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
          <p className="text-white/80 text-sm leading-relaxed">{t('aiMockup.botA1Plain')}</p>
          <div className="flex gap-2 mt-2">
            <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full">{t('aiMockup.tagDone')}</span>
            <span className="text-[10px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full">{t('aiMockup.tagPending')}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <div className="bg-primary/20 text-primary rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[80%]">
          <p className="text-sm">{t('aiMockup.userQ2')}</p>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="w-6 h-6 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0 mt-1">
          <Bot className="w-3.5 h-3.5 text-primary" />
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-primary" />
            <p className="text-white/80 text-sm font-medium">{t('aiMockup.reportGenerated')}</p>
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <p className="text-white/50 text-xs">{t('aiMockup.reportDesc')}</p>
        </div>
      </div>
    </div>

    {/* Input */}
    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
      <Mic className="w-4 h-4 text-white/30" />
      <span className="text-white/30 text-sm flex-1">{t('aiMockup.inputPlaceholder')}</span>
      <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
        <Send className="w-3.5 h-3.5 text-white" />
      </div>
    </div>
  </div>
  );
};

export const AIAssistantChatMockup = () => {
  const { t } = useTranslation();
  return (
  <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-md border border-gray-100" dir="ltr">
    {/* Header */}
    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
        <Bot className="w-4 h-4 text-primary" />
      </div>
      <p className="font-semibold text-sm text-gray-900">{t('aiMockup.chatTitle')}</p>
    </div>

    {/* Messages */}
    <div className="space-y-3 mb-4">
      <div className="flex justify-end">
        <div className="bg-primary text-white rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[80%]">
          <p className="text-sm">{t('aiMockup.chatUserQ')}</p>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
          <Bot className="w-3.5 h-3.5 text-primary" />
        </div>
        <div className="bg-gray-50 border border-gray-100 rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-primary" />
            <p className="text-gray-800 text-sm font-medium">{t('aiMockup.invoiceCreated')}</p>
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
          </div>
          <p className="text-gray-500 text-xs mb-2">{t('aiMockup.invoiceDesc')}</p>
          <div className="flex gap-2">
            <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{t('aiMockup.viewInvoice')}</span>
            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{t('aiMockup.exportPdf')}</span>
          </div>
        </div>
      </div>
    </div>

    {/* Input */}
    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
      <Mic className="w-4 h-4 text-gray-300" />
      <span className="text-gray-400 text-sm flex-1">{t('aiMockup.chatPlaceholder')}</span>
      <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
        <Send className="w-3.5 h-3.5 text-white" />
      </div>
    </div>
  </div>
  );
};
